import React, { useState, useMemo, useRef } from 'react';

const STAGE_SHORT_OVERRIDES = {
  'Coachella Stage': 'Coach',
  'Outdoor Theatre': 'Outdoor',
  'Do LaB': 'DoLaB',
  'Mane Stage': 'Mane',
  "Diplo's Honkytonk": 'Honky',
  'Toyota Music Den': 'Toyota',
  "Guy Fieri's Smokehouse": 'Smoke',
  'Bud Light Stage': 'BudLt',
};
const shortStageName = (stage) => STAGE_SHORT_OVERRIDES[stage] || stage;
const DAYS = ['friday', 'saturday', 'sunday'];
const DAY_LABELS = { friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

const START_HOUR = 13;
const END_HOUR = 25;
const PX_PER_HOUR = 80;

function getHourFromDate(isoString) {
  if (!isoString) return null;
  const d = new Date(isoString);
  let h = d.getHours();
  if (h < START_HOUR) h += 24;
  return h + d.getMinutes() / 60;
}

function formatTimeShort(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return minutes ? `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}` : `${hours} ${ampm}`;
}

function ScoreBadge({ score }) {
  if (!score) return null;
  let color = 'bg-white/20';
  if (score >= 70) color = 'bg-green-500';
  else if (score >= 40) color = 'bg-yellow-500';
  else if (score > 0) color = 'bg-orange-500';
  return <span className={`${color} text-white text-[9px] font-black px-1.5 py-0.5 rounded`}>{score}</span>;
}

// ── List View ───────────────────────────────────────────────────────────────

function ListView({ dayActs, selectedActIds, toggleAct, filter }) {
  let sorted = [...dayActs].sort((a, b) => {
    const aH = getHourFromDate(a.set_start) || 0;
    const bH = getHourFromDate(b.set_start) || 0;
    return bH - aH;
  });
  if (filter === 'selected') sorted = sorted.filter(a => selectedActIds.has(a.id));

  if (sorted.length === 0) {
    return <p className="text-white/20 text-sm text-center py-8">No acts {filter === 'selected' ? 'selected' : 'available'}</p>;
  }

  return (
    <div className="space-y-1.5 px-4 py-2">
      {sorted.map(act => {
        const selected = selectedActIds.has(act.id);
        return (
          <div
            key={act.id}
            onClick={() => toggleAct(act.id)}
            className={`flex items-center gap-3 rounded-xl px-3 py-2 cursor-pointer transition-all border ${
              selected ? 'bg-white/15 border-white/30' : 'bg-white/5 border-white/5 opacity-50'
            }`}
          >
            {act.artist_image_url ? (
              <img src={act.artist_image_url} alt="" className="w-9 h-9 rounded-full ring-1 ring-white/20 object-cover flex-shrink-0" loading="lazy" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/30 font-bold text-xs flex-shrink-0">
                {(act.artist_name || '?')[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className={`font-medium text-sm leading-tight truncate ${selected ? 'text-white' : 'text-white/60'}`}>{act.artist_name}</p>
              <p className="text-white/40 text-[11px]">
                {act.stage} &middot; {formatTimeShort(act.set_start)}{act.set_end ? ` – ${formatTimeShort(act.set_end)}` : ''}
              </p>
            </div>
            {act.score > 0 && <ScoreBadge score={act.score} />}
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
              selected ? 'border-white bg-white' : 'border-white/20'
            }`}>
              {selected && (
                <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Grid View ───────────────────────────────────────────────────────────────

function GridView({ dayActs, selectedActIds, toggleAct, stages }) {
  const gridHeight = (END_HOUR - START_HOUR) * PX_PER_HOUR;

  const timeLabels = [];
  for (let h = END_HOUR - 1; h >= START_HOUR; h--) {
    const displayHour = h > 24 ? h - 24 : h > 12 ? h - 12 : h === 0 ? 12 : h;
    const ampm = (h >= 12 && h < 24) ? 'PM' : 'AM';
    timeLabels.push({ hour: h, label: `${displayHour}${ampm}` });
  }

  return (
    <div className="flex" style={{ minWidth: stages.length * 85 + 44 }}>
      <div className="sticky left-0 z-10 w-[44px] flex-shrink-0" style={{ backgroundColor: 'var(--ln-bg, #000)' }}>
        <div className="h-7" />
        <div className="relative" style={{ height: gridHeight }}>
          {timeLabels.map(({ hour, label }) => (
            <div key={hour} className="absolute left-0 right-0 text-white/20 text-[9px] text-right pr-1 border-t border-white/5"
              style={{ top: (END_HOUR - 1 - hour) * PX_PER_HOUR, height: PX_PER_HOUR }}>
              {label}
            </div>
          ))}
        </div>
      </div>
      {stages.map(stage => {
        const stageActs = dayActs.filter(a => a.stage === stage);
        return (
          <div key={stage} className="flex-shrink-0" style={{ width: 85 }}>
            <div className="h-7 flex items-center justify-center border-b border-white/10 sticky top-0 z-5" style={{ backgroundColor: 'var(--ln-bg, #000)' }}>
              <span className="text-white/40 text-[8px] font-bold uppercase truncate px-0.5">{shortStageName(stage)}</span>
            </div>
            <div className="relative border-l border-white/5" style={{ height: gridHeight }}>
              {timeLabels.map(({ hour }) => (
                <div key={hour} className="absolute left-0 right-0 border-t border-white/5" style={{ top: (END_HOUR - 1 - hour) * PX_PER_HOUR, height: PX_PER_HOUR }} />
              ))}
              {stageActs.map(act => {
                const startH = getHourFromDate(act.set_start);
                const endH = act.set_end ? getHourFromDate(act.set_end) : startH + 1.5;
                if (startH === null) return null;
                const top = (END_HOUR - endH) * PX_PER_HOUR;
                const height = (endH - startH) * PX_PER_HOUR;
                const selected = selectedActIds.has(act.id);
                return (
                  <div key={act.id} onClick={() => toggleAct(act.id)}
                    className={`absolute left-0.5 right-0.5 rounded-md cursor-pointer transition-all overflow-hidden border ${
                      selected ? 'bg-white/20 border-white/40 shadow-lg' : 'bg-white/5 border-white/10 opacity-40 hover:opacity-60'
                    }`}
                    style={{ top: Math.max(0, top), height: Math.max(24, height - 2) }}>
                    <div className="px-1 py-0.5 flex flex-col h-full">
                      {height > 55 && act.artist_image_url && (
                        <img src={act.artist_image_url} alt="" className="w-5 h-5 rounded-full ring-1 ring-white/20 object-cover mb-0.5 flex-shrink-0" loading="lazy" />
                      )}
                      <p className={`font-bold leading-tight ${height > 40 ? 'text-[9px] line-clamp-2' : 'text-[7px] truncate'} ${selected ? 'text-white' : 'text-white/60'}`}>
                        {act.artist_name}
                      </p>
                      {height > 35 && (
                        <div className="flex items-center justify-between mt-auto">
                          <p className="text-white/30 text-[7px] truncate">{formatTimeShort(act.set_start)}</p>
                          {act.score > 0 && <span className={`text-[6px] font-black px-1 rounded ${act.score >= 70 ? 'bg-green-500/80 text-white' : act.score >= 40 ? 'bg-yellow-500/80 text-black' : 'bg-white/20 text-white'}`}>{act.score}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main ScheduleBuilder ────────────────────────────────────────────────────

export default function ScheduleBuilder({ acts, optimalSchedule, initialSelectedIds, startFresh, onComplete, onCancel }) {
  const [currentDay, setCurrentDay] = useState(0);
  const [selectedActIds, setSelectedActIds] = useState(() => {
    if (initialSelectedIds?.length) return new Set(initialSelectedIds);
    return new Set();
  });
  const [started, setStarted] = useState(!!initialSelectedIds?.length || !!startFresh);
  // Default: list on mobile, grid on desktop
  const [viewMode, setViewMode] = useState(typeof window !== 'undefined' && window.innerWidth >= 768 ? 'grid' : 'list');
  const [filter, setFilter] = useState('all'); // 'all' | 'selected'
  const gridRef = useRef(null);

  const day = DAYS[currentDay];

  const dayActs = useMemo(() => {
    return (acts || []).filter(a => a.day === day && a.set_start && a.stage);
  }, [acts, day]);

  const stages = useMemo(() => {
    const seen = [];
    for (const a of (acts || [])) {
      if (a.stage && !seen.includes(a.stage)) seen.push(a.stage);
    }
    return seen;
  }, [acts]);

  const toggleAct = (actId) => {
    setSelectedActIds(prev => {
      const next = new Set(prev);
      if (next.has(actId)) next.delete(actId);
      else next.add(actId);
      return next;
    });
  };

  const handleStartOptimal = () => {
    if (optimalSchedule) {
      const ids = new Set();
      for (const d of DAYS) {
        (optimalSchedule[d] || []).forEach(a => { if (a.act_id) ids.add(a.act_id); });
      }
      setSelectedActIds(ids);
    }
    setStarted(true);
  };

  const handleStartFresh = () => {
    setSelectedActIds(new Set());
    setStarted(true);
  };

  const handleNext = () => {
    if (currentDay < 2) {
      setCurrentDay(d => d + 1);
      if (gridRef.current) gridRef.current.scrollTop = 0;
    } else {
      const selected = (acts || [])
        .filter(a => selectedActIds.has(a.id))
        .map(a => ({ act_id: a.id, day: a.day, score: a.score || 0 }));
      onComplete(selected);
    }
  };

  const handlePrev = () => {
    if (currentDay > 0) {
      setCurrentDay(d => d - 1);
      if (gridRef.current) gridRef.current.scrollTop = 0;
    }
  };

  const selectedCount = dayActs.filter(a => selectedActIds.has(a.id)).length;
  const totalSelected = (acts || []).filter(a => selectedActIds.has(a.id)).length;

  if (!started) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <h2 className="text-white font-black text-xl uppercase mb-2">Build Your Schedule</h2>
        <p className="text-white/40 text-sm mb-8">Select the artists you want to see</p>
        <div className="space-y-3 w-full max-w-xs">
          {optimalSchedule && (
            <button onClick={handleStartOptimal} className="w-full bg-white/10 border border-white/20 text-white font-bold text-sm py-3 px-6 rounded-xl hover:bg-white/20 transition-all">
              Start from Optimal Schedule
            </button>
          )}
          <button onClick={handleStartFresh} className="w-full bg-white/5 border border-white/10 text-white/60 font-bold text-sm py-3 px-6 rounded-xl hover:bg-white/10 transition-all">
            Start Fresh
          </button>
          <button onClick={onCancel} className="w-full text-white/30 text-xs py-2 hover:text-white/50 transition-all">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full -mx-4 lg:-mx-8">
      {/* Header */}
      <div className="flex-shrink-0 px-4 pt-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h2 className="text-white font-black text-lg uppercase">{DAY_LABELS[day]}</h2>
            <button
              onClick={() => setFilter(filter === 'selected' ? 'all' : 'selected')}
              className={`text-xs font-bold px-2 py-0.5 rounded-full transition-all ${
                filter === 'selected' ? 'bg-white/20 text-white border border-white/30' : 'text-white/40 border border-white/20 hover:text-white/60 hover:border-white/30'
              }`}
            >
              {filter === 'selected' ? `✕ ${selectedCount} selected` : `☑ ${selectedCount} selected`}
            </button>
          </div>
          {/* View toggle */}
          <div className="flex bg-white/5 rounded-lg p-0.5">
            {['list', 'grid'].map(mode => (
              <button
                key={mode}
                onClick={() => { setViewMode(mode); if (filter === 'selected' && mode === 'grid') setFilter('all'); }}
                className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase transition-all ${
                  viewMode === mode ? 'bg-white/15 text-white' : 'text-white/30'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
        </div>

        {/* Day tabs */}
        <div className="flex border-b border-white/10">
          {DAYS.map((d, i) => {
            const daySelCount = (acts || []).filter(a => a.day === d && selectedActIds.has(a.id)).length;
            return (
              <button
                key={d}
                onClick={() => { setCurrentDay(i); if (gridRef.current) gridRef.current.scrollTop = 0; }}
                className={`flex-1 py-1.5 text-xs font-bold uppercase tracking-wider text-center transition-colors ${
                  i === currentDay ? 'text-white border-b-2 border-white' : 'text-white/30'
                }`}
              >
                {DAY_LABELS[d]} {daySelCount > 0 && <span className="text-white/20">({daySelCount})</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div ref={gridRef} className="flex-1 overflow-auto min-h-0">
        {filter === 'selected' ? (
          <ListView dayActs={dayActs} selectedActIds={selectedActIds} toggleAct={toggleAct} filter="selected" />
        ) : viewMode === 'list' ? (
          <ListView dayActs={dayActs} selectedActIds={selectedActIds} toggleAct={toggleAct} filter="all" />
        ) : (
          <div className="flex justify-center">
            <GridView dayActs={dayActs} selectedActIds={selectedActIds} toggleAct={toggleAct} stages={stages} />
          </div>
        )}
      </div>

      {/* Bottom nav — fixed on mobile above nav bar, inline on desktop.
          Mobile nav height = 56px tab content + safe-area-inset-bottom (home
          indicator). Stack this row above that combined height. */}
      <div
        className="schedule-builder-nav fixed md:relative md:bottom-auto left-0 right-0 z-40 px-4 py-2 flex items-center justify-between border-t border-white/10"
        style={{ backgroundColor: 'var(--ln-bg, #000)' }}
      >
        <button
          onClick={currentDay > 0 ? handlePrev : onCancel}
          className="flex items-center gap-1.5 text-white/40 hover:text-white text-sm font-bold transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {currentDay > 0 ? DAY_LABELS[DAYS[currentDay - 1]] : 'Cancel'}
        </button>
        <span className="text-white/20 text-xs">{totalSelected} total</span>
        <button
          onClick={handleNext}
          className="flex items-center gap-1.5 bg-white text-black font-bold text-sm px-4 py-1.5 rounded-full hover:bg-white/90 transition-all"
        >
          {currentDay < 2 ? DAY_LABELS[DAYS[currentDay + 1]] : 'Complete'}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}
